import React, { useState, useEffect } from 'react';
import Text from '../Text/Text';

interface CountdownTimerProps {
  targetDate: Date;
  postTimeText?: string; // Optional prop to display time text after the countdown
  expiredText?: string; // Optional prop to display when the timer expires
}

/**
 * CountdownTimer component
 *
 * This component displays a countdown timer that shows the time remaining until
 * the specified target date. It updates every second and formats the time in a user-friendly way:
 * - For durations ≥ 1 day: Shows "X days Y hours" (e.g., "4 days 2 hours")
 * - For durations < 1 day but ≥ 1 hour: Shows hours, minutes, and seconds (HH:MM:SS)
 * - For durations < 1 hour: Shows minutes and seconds (MM:SS)
 */
export const CountdownTimer: React.FC<CountdownTimerProps> = ({ targetDate, postTimeText, expiredText }) => {
  const [timeRemaining, setTimeRemaining] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  const [isExpired, setIsExpired] = useState<boolean>(false);
  
  useEffect(() => {
    // Calculate initial time remaining
    const calculateTimeRemaining = () => {
      const now = new Date();
      const difference =  targetDate.getTime() - now.getTime() ;
      console.log('Current time:', now);
      console.log('Target time:', targetDate);
      console.log('Time remaining for auto release:', difference);

      if (difference <= 0) {
        // Timer has expired
        setIsExpired(true);
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }
      
      // Calculate days, hours, minutes, and seconds
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);
      
      return { days, hours, minutes, seconds };
    };
    
    // Update time remaining
    setTimeRemaining(calculateTimeRemaining());
    
    // Set up interval to update time remaining every second
    const intervalId = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);
      
      // Clear interval if timer has expired
      if (remaining.days === 0 && remaining.hours === 0 && remaining.minutes === 0 && remaining.seconds === 0) {
        setIsExpired(true);
        clearInterval(intervalId);
      }
    }, 1000);
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [targetDate]);
  
  // Format time as HH:MM:SS
  const formatTime = (hours: number, minutes: number, seconds: number) => {
    const formatNumber = (num: number) => num.toString().padStart(2, '0');
    return `${formatNumber(hours)}:${formatNumber(minutes)}:${formatNumber(seconds)}`;
  };
  
  // Format time in a user-friendly way based on duration
  const formatTimeDisplay = (days: number, hours: number, minutes: number, seconds: number) => {
    if (days > 0) {
      return `${days} ${days === 1 ? 'day' : 'days'} ${hours} ${hours === 1 ? 'hour' : 'hours'}`;
    } else if (hours > 0) {
      return formatTime(hours, minutes, seconds);
    } else {
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  };
  
  return (
    <div className="countdown-timer">
      {isExpired ? (
        <Text className="text-body text-primary timer-text">
          <strong>{expiredText || 'Expired'}</strong>
        </Text>
      ) : (
        <div className="countdown-display">
          <Text className="text-body text-primary timer-text">
            {formatTimeDisplay(
              timeRemaining.days,
              timeRemaining.hours,
              timeRemaining.minutes,
              timeRemaining.seconds
            )} {postTimeText}
          </Text>
        </div>
      )}
    </div>
  );
};